import * as React from 'react';
import { memo, useMemo, useCallback } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { IGroup, PublishedLmsType } from 'types';
import { PopupNames } from 'core';
import { usePopupStateOperations } from 'core/hooks';
import { PATHS } from 'router/config';

import { weekDays } from 'components/constants';
import { Switcher } from 'atoms';

import { GroupsTableComponent, IColumn, IRow } from './GroupsTable';

export interface IGroupsTable {
  data: IGroup[];
  loading?: boolean;
  error?: string;
  loadMore: () => void;
  hasMore: boolean;
}

const getColumnsSource = (t: TFunction): IColumn[] => [
  {
    Header: `${t('table.groups.number')}`,
    accessor: 'group',
    columnWidth: '208px',
  },
  {
    Header: `${t('table.groups.startDate')}`,
    accessor: 'startDate',
    columnWidth: '96px',
    marginRight: '56px',
    marginLeft: '80px',
  },
  {
    Header: `${t('table.groups.numberOfLessons')}`,
    accessor: 'numberOfLessons',
    columnWidth: '80px',
  },
  {
    Header: `${t('table.groups.numberOfStudents')}`,
    accessor: 'numberOfStudents',
    columnWidth: '88px',
    marginRight: '48px',
    marginLeft: '48px',
  },
  {
    Header: `${t('table.groups.lessonDays')}`,
    accessor: 'lessonDays',
    columnWidth: '100px',
    marginRight: '48px',
  },
  {
    Header: `${t('table.groups.agreementsSum')}`,
    accessor: 'agreementsSum',
    columnWidth: '142px',
    marginRight: '48px',
  },
];

/**
 * Local wrapper for the Switcher cells: the row itself is now
 * clickable (opens the group control center), so the switch click
 * has to stop propagation, otherwise toggling LMS publication would
 * also navigate. Stays inside the container — `Switcher` atom keeps
 * its event-neutral contract for other consumers.
 */
const SwitcherCell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span
    role="presentation"
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
  >
    {children}
  </span>
);

export const GroupsTable = memo(
  ({ data, loading, error, loadMore, hasMore }: IGroupsTable) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();

    const columns: IColumn[] = useMemo(
      () => [
        ...getColumnsSource(t),
        {
          Header: `${t('table.groups.teacher')}`,
          accessor: 'publishedLmsForTeacher',
          columnWidth: '30px',
          marginRight: '48px',
          Cell: ({ row }) => (
            <SwitcherCell>
              <Switcher
                checked={row.original.publishedLmsForTeacher}
                onClick={(checked) =>
                  checked
                    ? showPopup(PopupNames.addGroupInLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.teacher,
                      })
                    : showPopup(PopupNames.removeGroupFromLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.teacher,
                      })
                }
              />
            </SwitcherCell>
          ),
        },
        {
          Header: `${t('table.groups.student')}`,
          accessor: 'publishedLms',
          columnWidth: '30px',
          marginRight: '48px',
          Cell: ({ row }) => (
            <SwitcherCell>
              <Switcher
                checked={row.original.publishedLms}
                onClick={(checked) =>
                  checked
                    ? showPopup(PopupNames.addGroupInLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.student,
                      })
                    : showPopup(PopupNames.removeGroupFromLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.student,
                      })
                }
              />
            </SwitcherCell>
          ),
        },
        {
          Header: `${t('table.groups.methodist')}`,
          accessor: 'publishedLmsForMethodist',
          columnWidth: '30px',
          marginRight: '56px',
          Cell: ({ row }) => (
            <SwitcherCell>
              <Switcher
                checked={row.original.publishedLmsForMethodist}
                onClick={(checked) =>
                  checked
                    ? showPopup(PopupNames.addGroupInLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.methodist,
                      })
                    : showPopup(PopupNames.removeGroupFromLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.methodist,
                      })
                }
              />
            </SwitcherCell>
          ),
        },
      ],
      [showPopup, t],
    );
    const rows: IRow[] = useMemo(
      () =>
        data.map((debtor, idx) => ({
          ...debtor,
          num: idx + 1,
          lessonDays: debtor.lessonDays.map((num) => weekDays[num]).join(', '),
        })),
      [data],
    );

    const onEditButtonClick = useCallback(
      (groupId: string) => {
        showPopup(PopupNames.editGroupPopup, groupId);
      },
      [showPopup],
    );

    const onOpenDetail = useCallback(
      (groupId: string) => {
        history.push(PATHS.groupDetail.replace(':id', groupId));
      },
      [history],
    );

    if (error) {
      return <p>{error}</p>;
    }

    return (
      <GroupsTableComponent
        rows={rows}
        columns={columns}
        isFlexible
        noDataText={t('table.groups.noData')}
        loadMore={loadMore}
        hasMore={hasMore}
        onEditButtonClick={onEditButtonClick}
        onOpenDetail={onOpenDetail}
        editButtonLabel={t('table.groups.editAction')}
        loading={loading}
      />
    );
  },
);
